import { createFileRoute, useRouter } from "@tanstack/react-router";
import { openEntityModal } from "@/utils/noteNavigation";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, lazy, Suspense } from "react";
import { useDocumentTitle } from "usehooks-ts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Network,
  Search,
  Users,
  Building2,
  MapPin,
  Tag,
  Film,
  Tv,
  Calendar,
  Loader2,
  ChevronRight,
} from "lucide-react";
import * as entitiesApi from "@/lib/api/entities";
import { EntityRelationshipBrowser } from "@/components/EntityRelationshipBrowser";
import { PageHeader } from "@/components/layout/page-header";
import { UnauthenticatedPage } from "@/components/layout/unauthenticated-page";

const EntityGraph = lazy(() =>
  import("@/components/EntityGraph").then((mod) => ({
    default: mod.EntityGraph,
  })),
);

export const Route = createFileRoute("/entities")({
  component: EntitiesPage,
});

const ENTITY_TYPE_ICONS: Record<string, React.ReactNode> = {
  PERSON: <Users className="h-4 w-4" />,
  ORGANIZATION: <Building2 className="h-4 w-4" />,
  LOCATION: <MapPin className="h-4 w-4" />,
  TOPIC: <Tag className="h-4 w-4" />,
  MOVIE: <Film className="h-4 w-4" />,
  TV_SHOW: <Tv className="h-4 w-4" />,
  EVENT: <Calendar className="h-4 w-4" />,
};

const ENTITY_TYPE_COLORS: Record<string, string> = {
  PERSON: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  ORGANIZATION: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  LOCATION: "bg-green-500/10 text-green-600 border-green-500/20",
  TOPIC: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  MOVIE: "bg-red-500/10 text-red-600 border-red-500/20",
  TV_SHOW: "bg-pink-500/10 text-pink-600 border-pink-500/20",
  EVENT: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
};

interface Entity {
  id: string;
  name: string;
  entityType: {
    id: string;
    name: string;
  };
  _count: {
    notes: number;
    fromRelationships: number;
    toRelationships: number;
  };
}

function EntitiesPage() {
  const { user } = Route.useRouteContext();
  const router = useRouter();
  useDocumentTitle("Entities | Notebook");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [activeTab, setActiveTab] = useState("browse");

  const handleOpenEntityModal = (entityId: string) => {
    openEntityModal(router, entityId);
  };

  const { data: entities = [], isLoading } = useQuery({
    queryKey: ["entities", "all"],
    queryFn: () => entitiesApi.getAllEntities() as Promise<Entity[]>,
    enabled: !!user?.id,
  });

  const { data: entityDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["entity", selectedEntity?.id, "relationships"],
    queryFn: () => entitiesApi.getEntityRelationships(selectedEntity!.id),
    enabled: !!selectedEntity?.id,
  });

  const filteredEntities = useMemo(() => {
    if (!searchQuery.trim()) return entities;
    const query = searchQuery.toLowerCase();
    return entities.filter((e) => e.name?.toLowerCase().includes(query));
  }, [entities, searchQuery]);

  const groupedEntities = useMemo(() => {
    const groups: Record<string, Entity[]> = {};
    filteredEntities.forEach((entity) => {
      const type = entity.entityType?.name || "UNKNOWN";
      if (!groups[type]) groups[type] = [];
      groups[type].push(entity);
    });
    Object.keys(groups).forEach((type) => {
      groups[type].sort((a, b) => b._count.notes - a._count.notes);
    });
    return groups;
  }, [filteredEntities]);

  const stats = useMemo(() => {
    const totalEntities = entities.length;
    const totalRelationships = entities.reduce(
      (sum, e) =>
        sum +
        (e._count.fromRelationships || 0) +
        (e._count.toRelationships || 0),
      0,
    );
    const typeBreakdown = Object.entries(groupedEntities).map(
      ([type, items]) => ({
        type,
        count: items.length,
      }),
    );
    return {
      totalEntities,
      totalRelationships: totalRelationships / 2,
      typeBreakdown,
    };
  }, [entities, groupedEntities]);

  if (!user?.id) {
    return (
      <UnauthenticatedPage
        title="Entities"
        description="Sign in to explore your entity relationships"
        icon={Network}
        iconColor="text-primary"
      />
    );
  }

  return (
    <>
      <PageHeader title="Entities" icon={Network} iconColor="text-primary" />

      <div className="flex-1 space-y-6 px-4 md:px-6 w-full max-w-6xl mx-auto pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold font-display">
                {stats.totalEntities}
              </div>
              <div className="text-sm text-muted-foreground">
                Total Entities
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold font-display">
                {Math.round(stats.totalRelationships)}
              </div>
              <div className="text-sm text-muted-foreground">Relationships</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold font-display">
                {stats.typeBreakdown.length}
              </div>
              <div className="text-sm text-muted-foreground">Entity Types</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold font-display">
                {entities.filter((e) => e._count.notes > 1).length}
              </div>
              <div className="text-sm text-muted-foreground">Multi-note</div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="browse">Browse</TabsTrigger>
            <TabsTrigger value="search">Search</TabsTrigger>
            <TabsTrigger value="graph">Graph</TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="space-y-4 mt-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter entities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  Object.entries(groupedEntities).map(([type, items]) => (
                    <Card key={type}>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base">
                          {ENTITY_TYPE_ICONS[type] || (
                            <Tag className="h-4 w-4" />
                          )}
                          {type.replace(/_/g, " ")}
                          <Badge variant="secondary" className="ml-auto">
                            {items.length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {items.slice(0, 12).map((entity) => (
                            <button
                              key={entity.id}
                              onClick={() => handleOpenEntityModal(entity.id)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-full border transition-colors hover:opacity-80 ${
                                selectedEntity?.id === entity.id
                                  ? "ring-2 ring-primary"
                                  : ""
                              } ${ENTITY_TYPE_COLORS[type] || "bg-muted"}`}
                            >
                              {entity.name}
                              {entity._count.notes > 1 && (
                                <span className="text-xs opacity-60">
                                  ({entity._count.notes})
                                </span>
                              )}
                            </button>
                          ))}
                          {items.length > 12 && (
                            <span className="text-sm text-muted-foreground px-2 py-1">
                              +{items.length - 12} more
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              <div>
                {selectedEntity ? (
                  <Card className="sticky top-4">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {ENTITY_TYPE_ICONS[selectedEntity.entityType?.name] || (
                          <Tag className="h-4 w-4" />
                        )}
                        {selectedEntity.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            ENTITY_TYPE_COLORS[
                              selectedEntity.entityType?.name
                            ] || "bg-muted"
                          }
                        >
                          {selectedEntity.entityType?.name?.replace(/_/g, " ")}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {selectedEntity._count.notes} note
                          {selectedEntity._count.notes !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {isLoadingDetails ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : entityDetails?.relationships?.length ? (
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium">Relationships</h4>
                          <div className="space-y-2">
                            {entityDetails.relationships.map((rel: any) => (
                              <div
                                key={rel.id}
                                className="flex items-center gap-2 text-sm p-2 rounded-md bg-muted/50"
                              >
                                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                <span className="text-muted-foreground">
                                  {rel.relationshipType}
                                </span>
                                <button
                                  onClick={() =>
                                    setSelectedEntity(rel.relatedEntity)
                                  }
                                  className="text-primary hover:underline"
                                >
                                  {rel.relatedEntity.name}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No relationships found
                        </p>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setSelectedEntity(null)}
                      >
                        Clear Selection
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <Network className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Select an entity to see its relationships</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="search" className="mt-4">
            <EntityRelationshipBrowser />
          </TabsContent>

          <TabsContent value="graph" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Network className="h-4 w-4" />
                  Entity Graph
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  }
                >
                  <EntityGraph
                    onEntityClick={(entity) => handleOpenEntityModal(entity.id)}
                  />
                </Suspense>
                <div className="mt-4 flex flex-wrap gap-2 text-sm">
                  {Object.entries(ENTITY_TYPE_COLORS).map(
                    ([type, className]) => (
                      <div key={type} className="flex items-center gap-1.5">
                        <div
                          className={`w-3 h-3 rounded-full ${className.split(" ")[0]}`}
                        />
                        <span className="text-muted-foreground">
                          {type.replace(/_/g, " ")}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
